import { Percentage } from '@sparkdotfi/common-universal'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { SparkTokenNavLinkContent } from './components/SparkTokenNavLinkContent'
import { LINKS_DATA } from './constants'
import { TopbarButton } from './TopbarButton'
import { formatPercentage } from '@/domain/common/format'
import { SavingsAPYBadge } from '@/features/savings/components/navbar-item/SavingsAPYBadge'
import { SavingsConverterQueryResults } from '@/features/topbar/types'
import { Route } from '@/routing/router'
import { InternalLink as InternalLinkType } from '@/routing/types'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/atoms/dropdown/DropdownMenu'
import { InternalLink } from '@/ui/atoms/link/Link'
import { cn } from '@/ui/utils/style'

export interface TopbarNavigationProps {
  savingsConverter: SavingsConverterQueryResults | undefined
  blockedPages: Route[]
  sparkLendSubLinks: {
    link: InternalLinkType
    label: string
  }[]
  spkTokenSubLinks: {
    link: InternalLinkType
    label: string
    standaloneLabel: string
    description: string
    apy?: Percentage
  }[]
  isSparkLendSubLinkActive: boolean
  isSparkTokenSubLinkActive: boolean
}

export function TopbarNavigation({
  savingsConverter,
  blockedPages,
  sparkLendSubLinks,
  spkTokenSubLinks,
  isSparkLendSubLinkActive,
  isSparkTokenSubLinkActive,
}: TopbarNavigationProps) {
  const [sparkLendDropdownOpen, setSparkLendDropdownOpen] = useState(false)
  const [sparkTokenDropdownOpen, setSparkTokenDropdownOpen] = useState(false)

  function closeTopBarDropdowns() {
    setSparkLendDropdownOpen(false)
    setSparkTokenDropdownOpen(false)
  }

  return (
    <div className="hidden gap-2 sm:flex">
      {!blockedPages.some((page) => page === '/' || page === '/savings/$domain/$savingsToken') && ( // some instead of includes for better type inference
        <TopbarButton
          to="/"
          activeRoutes={['/savings/$domain/$savingsToken']}
          label={LINKS_DATA.savings.label}
          prefixIcon={LINKS_DATA.savings.icon}
          type="savings"
          postfixSlot={
            savingsConverter?.data || savingsConverter?.isLoading ? (
              <SavingsAPYBadge
                APY={savingsConverter.data?.apy}
                isLoading={savingsConverter.isLoading}
                className="top-bar-xl:inline-flex hidden shrink-0"
              />
            ) : undefined
          }
        />
      )}

      <DropdownMenu open={sparkLendDropdownOpen} onOpenChange={setSparkLendDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <TopbarButton
            label={LINKS_DATA.sparkLend.label}
            type="sparkLend"
            prefixIcon={LINKS_DATA.sparkLend.icon}
            postfixSlot={
              <ChevronDown
                className={cn('shrink-0 transition-transform duration-300', sparkLendDropdownOpen && 'rotate-180')}
              />
            }
            active={isSparkLendSubLinkActive}
            highlighted={sparkLendDropdownOpen}
          />
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-72 shadow-2xl" align="start">
          {sparkLendSubLinks.map(({ link, label }) => (
            <DropdownMenuItem
              key={link.to}
              asChild
              className="cursor-pointer rounded-none border-b border-b-primary p-0 first:rounded-t-xs last:rounded-b-xs last:border-none"
            >
              <InternalLink {...link} onClick={closeTopBarDropdowns} variant="unstyled">
                {({ isActive }) => (
                  <div
                    className={cn('relative w-full p-6 text-secondary hover:text-primary', isActive && 'text-primary')}
                  >
                    <div
                      className={cn(
                        // @note: one time use gradient
                        'absolute top-0 bottom-0 left-0 hidden w-[3px] bg-linear-to-br from-[#FFCD4D] to-[#FF895D] opacity-0',
                        isActive && 'block opacity-100',
                      )}
                    />
                    {label}
                  </div>
                )}
              </InternalLink>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {spkTokenSubLinks.length === 1 && (
        <TopbarButton
          to={spkTokenSubLinks[0]!.link.to}
          label={spkTokenSubLinks[0]!.standaloneLabel}
          type="sparkToken"
          prefixIcon={LINKS_DATA.sparkToken.icon}
        />
      )}
      {spkTokenSubLinks.length > 1 && (
        <DropdownMenu open={sparkTokenDropdownOpen} onOpenChange={setSparkTokenDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <TopbarButton
              label={LINKS_DATA.sparkToken.label}
              type="sparkToken"
              prefixIcon={LINKS_DATA.sparkToken.icon}
              postfixSlot={
                <ChevronDown
                  className={cn('shrink-0 transition-transform duration-300', sparkTokenDropdownOpen && 'rotate-180')}
                />
              }
              active={isSparkTokenSubLinkActive}
              highlighted={sparkTokenDropdownOpen}
            />
          </DropdownMenuTrigger>

          <DropdownMenuContent className="w-72 shadow-2xl" align="start">
            {spkTokenSubLinks.map(({ link, label, description, apy }) => (
              <DropdownMenuItem
                key={link.to}
                asChild
                className="cursor-pointer rounded-none border-b border-b-primary p-0 first:rounded-t-xs last:rounded-b-xs last:border-none"
              >
                <InternalLink {...link} onClick={closeTopBarDropdowns} variant="unstyled">
                  {({ isActive }) => (
                    <SparkTokenNavLinkContent isActive={isActive}>
                      <div className="flex flex-col gap-2">
                        <div className="typography-label-2 text-primary">{label}</div>
                        <div className="typography-body-4 text-secondary">{description}</div>
                      </div>
                      {apy && (
                        <div className="typography-label-3 rounded-full bg-gradient-spark-primary-2 px-1.5 py-0.5 text-primary-inverse">
                          {formatPercentage(apy)}
                        </div>
                      )}
                    </SparkTokenNavLinkContent>
                  )}
                </InternalLink>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {import.meta.env.VITE_FEATURE_SWAP_PAGE === '1' && (
        <TopbarButton to="/swap" label={LINKS_DATA.swap.label} prefixIcon={LINKS_DATA.swap.icon} type="swap" />
      )}
    </div>
  )
}
